import { observer } from "mobx-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Form, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";

import { useAppStore } from "../../../providers/AppStoreProvider";
import { useAPI } from "../../../providers/ApiProvider";
import { toastWithAsyncFetch } from "../../../utils/helper";
import { TOASTIFY_OPTIONS } from "../../../config/ReactToastify";
import { Resource } from "../../Resource/Resource";
import { CreateResource } from "../../Resource/CreateResource";

export const AdditionalForm = observer(({ currentLesson }) => {
  const store = useAppStore();
  const api = useAPI();
  const [resources, setResources] = useState([]);
  const [shortcuts, setShortcuts] = useState([]);

  const shortcutNameRef = useRef(null);
  const shortcutKeysRef = useRef(null);
  const resourceInputRef = useRef(null);

  useEffect(() => {
    if (store.lessonModal.show && store.lessonModal.mode === "edit") {
      setResources(currentLesson.resources);
      setShortcuts(currentLesson.keyboard_shortcuts);
    }

    if (store.lessonModal.mode === "create") {
      setResources([]);
      setShortcuts([]);
    }
  }, [store.lessonModal.show]);

  /**
   * Resource handlers
   */

  const createResourceHandler = (resource) => {
    toastWithAsyncFetch(
      {
        loading: "Đang tạo thêm tài nguyên...",
        success: "Thêm tài nguyên thành công",
        error: "Tạo tài nguyên thất bại!",
      },
      api.callApi.bind(null, "createResource", {
        params: { lessonId: currentLesson.id },
        body: { ...resource, type: "link" },
      }),
      (res) => {
        currentLesson.addResource(res.data.data.resource);
        resourceInputRef.current.setResource({ name: "", link: "" });
      },
    );
  };

  const updateResourceHandler = async (resource) => {
    return await toastWithAsyncFetch(
      {
        loading: "Đang sửa tài nguyên...",
        success: "Sửa tài nguyên thành công",
        error: "Sửa tài nguyên thất bại!",
      },
      api.callApi.bind(null, "updateResource", {
        params: { pk: resource.id },
        body: { ...resource, type: "link" },
      }),
      (res) => {
        currentLesson.updateResource(resource.id, res.data.data.resource);
      },
    );
  };

  const deleteResourceHandler = (id) => {
    toastWithAsyncFetch(
      {
        loading: "Đang xoá tài nguyên...",
        success: "Xoá tài nguyên thành công",
        error: "Xoá tài nguyên thất bại!",
      },
      api.callApi.bind(null, "deleteResource", { params: { pk: id } }),
      (res) => {
        currentLesson.deleteResource(id);
      },
    );
  };

  const chapterApplyResourceHanlder = (resource) => {
    currentLesson.chapter.lessons.forEach((l) => {
      if (!l.resources.find((r) => r.id === resource.id)) {
        l.addResource({ ...resource });
      }
    });
  };

  /**
   * Shortcut handlers
   */

  const createShortcutHandler = () => {
    const shortcutDescription = shortcutNameRef.current.value;
    const shortcutKeys = shortcutKeysRef.current.value;

    if (!shortcutDescription || !shortcutKeys) {
      toast.warning("Điền đủ thông tin", TOASTIFY_OPTIONS);
      return;
    }

    toastWithAsyncFetch(
      {
        loading: "Đang xoá phím tắt...",
        success: "Xoá phím tắt thành công",
        error: "Xoá phím tắt thất bại!",
      },
      api.callApi.bind(null, "createShortcut", {
        params: { lessonId: currentLesson.id },
        body: {
          name: shortcutDescription,
          keystrokes: shortcutKeys,
        },
      }),
      (res) => {
        currentLesson.addShortcut(res.data.data.keyboard_shortcut);
        shortcutNameRef.current.value = "";
        shortcutKeysRef.current.value = "";
      },
    );
  };

  const deleteShortcutHandler = (id) => {
    toastWithAsyncFetch(
      {
        loading: "Đang xoá phím tắt...",
        success: "Xoá phím tắt thành công",
        error: "Xoá phím tắt thất bại!",
      },
      api.callApi.bind(null, "deleteShortcut", { params: { pk: id } }),
      (res) => {
        currentLesson.deleteShortcut(id);
      },
    );
  };

  return (
    <>
      <Form.Group className="mt-5">
        <Form.Label className="fw-bold">Tài nguyên</Form.Label>
        <Row xs={{ cols: 1 }} className="g-2">
          {resources.map((resource) => (
            <Col key={resource.id}>
              <Resource
                resource={resource}
                mode="edit"
                onDelete={deleteResourceHandler}
                onUpdate={updateResourceHandler}
                onChapterApply={chapterApplyResourceHanlder}
              />
            </Col>
          ))}
          <Col>
            <CreateResource ref={resourceInputRef} onSubmit={createResourceHandler} />
          </Col>
        </Row>
      </Form.Group>

      <Form.Group className="mt-5">
        <Form.Label className="fw-bold">Phím tắt cần nhớ</Form.Label>
        <div className="d-flex flex-column flex-lg-row">
          <Form.Control
            ref={shortcutNameRef}
            placeholder="Ví dụ: Phóng to"
            className="rounded-10 p-3 py-2"
          />
          <Form.Control
            ref={shortcutKeysRef}
            placeholder="Ví dụ: [Ctrl] + [+]"
            className="rounded-10 mt-2 m-lg-0 ms-lg-3 p-3 py-2"
          />
          <div
            role="button"
            className="add-shortcut-btn bg-gradient-orange text-nowrap rounded-10 mt-2 m-lg-0 ms-lg-3 px-4 py-2"
            onClick={createShortcutHandler}
          >
            Thêm phím tắt
          </div>
        </div>
      </Form.Group>

      <div className="mt-3">
        {shortcuts?.length === 0 ? (
          <div className="no-item rounded-10 text-black-50">Bài học chưa có phím tắt nào</div>
        ) : (
          <Table>
            <tbody>
              {shortcuts.map((shortcut) => (
                <tr key={shortcut.id}>
                  <td className="shortcut-text text-black-50">{shortcut.name}</td>
                  <td className="shortcut-keys">{shortcut.keystrokes}</td>
                  <td className="d-flex justify-content-end">
                    <div
                      role="button"
                      className="shortcut-action text-center rounded p-2"
                      onClick={() => deleteShortcutHandler(shortcut.id)}
                    >
                      <i className="fa-duotone fa-trash" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </>
  );
});
